import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { AgentMark } from '../ui/AgentMark';
import { LevelBadge } from '../ui/LevelBadge';
import { ProcessingTag } from '../ui/ProcessingTag';
import { WhyPopover } from '../ui/WhyPopover';
import { SlideToConfirm } from '../ui/SlideToConfirm';
import { RollingCounter } from '../ui/RollingCounter';
import { useDb } from '../../hooks/useDb';
import { db } from '../../utils/db';
import { pendingProposals, proposalLines } from '../../utils/dbSelectors';
import type { DeskStage } from './useDeskStage';
import type { Proposal } from '../../types/db';
const URGENCY: Record<Proposal['urgency'], string> = {
  nu: 'Nu',
  vandaag: 'Vandaag',
  'kan-wachten': 'Kan wachten'
};
const SLIDE: Record<Proposal['kind'], [string, string]> = {
  mail: ['Schuif om te versturen', 'Verstuurd'],
  code: ['Schuif om goed te keuren', 'Goedgekeurd'],
  boekhouding: ['Schuif om in te boeken', 'Ingeboekt']
};
function WaitingRow({
  p,
  stage
}: {
  p: Proposal;
  stage: DeskStage;
}) {
  const [open, setOpen] = useState(false);
  const lines = useDb(t => proposalLines(t, p), [p]);
  return <div role="listitem" className="rounded-2xl border border-white/[0.07] bg-night-900/70" data-id="element-676" data-mp="2vge9g4obmny4">
      <button type="button" aria-expanded={open} onClick={() => setOpen(v => !v)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left" data-id="element-677" data-mp="2qga2b59az5kk">
        <AgentMark agentId={p.agent} size="sm" data-id="element-678" data-mp="30f7mxx3lyeya" />
        <span className="min-w-0 flex-1 truncate text-[15px] text-ink" data-id="element-679" data-mp="30eg3fknac7od">{p.title}</span>
        <span className={`text-[13px] ${p.urgency === 'nu' ? 'text-amber' : 'text-ink-muted'}`} data-id="element-680" data-mp="3e5b62f76xsji">{URGENCY[p.urgency]}</span>
        <ChevronDownIcon className={`h-4 w-4 text-ink-muted transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden data-id="element-681" data-mp="3a5kggetehigk" />
      </button>
      <AnimatePresence initial={false} data-id="element-682" data-mp="3j5z2jge6v1l4">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-683" data-mp="3j3cp504m9ruz">
            <div className="px-3 pb-3" data-id="element-684" data-mp="2pqmnszuod7rq">
              <ul className="space-y-0.5 text-[14px] text-ink-soft" data-id="element-685" data-mp="2ujzcf9zctekh">
                {lines.map(l => <li key={l} className="line-clamp-2" data-id="element-686" data-mp="2zjbwxo2r69jw">{l}</li>)}
                {p.kind === 'code' && <li className="text-ink" data-id="element-687" data-mp="34gu7tk0byyuj">Mergen doe je daarna zelf.</li>}
              </ul>
              <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-688" data-mp="38ipbgv5qhobh">
                <LevelBadge level="naOk" data-id="element-689" data-mp="3diblywu6gqgl" />
                <ProcessingTag where={p.where} data-id="element-690" data-mp="3dh8xswo8p24d" />
                <span className="ml-auto" data-id="element-691" data-mp="3dfxrap3etg9k">
                  <WhyPopover traceId={p.traceId} data-id="element-692" data-mp="1e5zaxukv1gv4" />
                </span>
              </div>
              <div className="mt-3" data-id="element-693" data-mp="1j3het6k0t5w9">
                <SlideToConfirm label={SLIDE[p.kind][0]} confirmedLabel={SLIDE[p.kind][1]} onConfirm={() => {
              window.setTimeout(() => db.approveProposal(p.id), 450);
              stage.approved(p.agent, `${p.title} · ${SLIDE[p.kind][1].toLowerCase()}`);
            }} data-id="element-694" data-mp="1j4j2qbm4xmve" />
              </div>
              <div className="mt-2 flex gap-2" data-id="element-695" data-mp="wxgdcwkmsjn8">
                <button type="button" onClick={() => stage.showToast('Aanpassen opent het concept op het podium.', 'meldt')} className="h-10 flex-1 whitespace-nowrap rounded-full border border-white/10 text-[14px] text-ink-soft transition-colors duration-150 hover:border-white/20 hover:text-ink" data-id="element-696" data-mp="1ujdo27srrkom">
                  Aanpassen
                </button>
                <button type="button" onClick={() => {
              db.rejectProposal(p.id);
              stage.showToast('Afgewezen. Anna leert hiervan.', 'geweigerd');
            }} className="h-10 flex-1 whitespace-nowrap rounded-full text-[14px] text-ink-muted transition-colors duration-150 hover:bg-white/[0.05] hover:text-ink" data-id="element-697" data-mp="96j8g3r96mlh">
                  Afwijzen
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}

/** Bottom-right: Niveau 3 proposals, each expandable. */
export function WaitingStack({
  stage
}: {
  stage: DeskStage;
}) {
  const pending = useDb(pendingProposals);
  return <section aria-label="Wacht op jou" className="flex min-h-0 flex-col border-t border-white/[0.06] pt-4" data-id="element-698" data-mp="22r71lrgdyzsa">
      <div className="flex items-center justify-between px-3" data-id="element-699" data-mp="26sigp31mw2m8">
        <h2 className="text-[16px] font-semibold text-ink" data-id="element-700" data-mp="26qz35k7d45o5">Wacht op jou</h2>
        <span className="grid h-7 min-w-[28px] place-items-center rounded-full bg-white/[0.08] px-2 text-[14px] font-semibold text-ink" data-id="element-701" data-mp="rb8nh96cpnk7">
          <RollingCounter value={pending.length} data-id="element-702" data-mp="vbae8najswkp" />
        </span>
      </div>
      {pending.length === 0 ? <p className="px-3 pt-3 text-[14px] text-ink-muted" data-id="element-703" data-mp="iv17r8n675go">Niets wacht op je.</p> : <div role="list" className="no-scrollbar mt-3 min-h-0 space-y-2 overflow-y-auto" data-id="element-704" data-mp="ixdr9q8ktnvu">
          <AnimatePresence initial={false} data-id="element-705" data-mp="xpxpccgvd56f">
            {pending.map(p => <motion.div key={p.id} layout exit={{
          opacity: 0,
          x: -40,
          transition: {
            duration: 0.2
          }
        }} data-id="element-706" data-mp="styt62pauivg">
                <WaitingRow p={p} stage={stage} data-id="element-707" data-mp="51ibht9h4swo" />
              </motion.div>)}
          </AnimatePresence>
        </div>}
    </section>;
}